// Autenticação real via CRM Lukos (@crm/api), que faz proxy para o login do painel.
//
// ATENÇÃO (local apenas por enquanto): o token retornado por /api/auth/login carrega, no campo
// `host`, a connection string completa do SQL Server de produção (usuário e senha inclusos).
// Não é um bug deste projeto — é assim que o CRM emite o token hoje — mas enquanto isso não for
// corrigido do lado do CRM, este fluxo não deve ir para produção: qualquer pessoa logada no site
// pode decodificar o próprio token (JWT não é criptografado) e ler a senha do banco do painel.
const CRM_API_URL = (import.meta.env.VITE_CRM_API_URL || 'http://localhost:3336').replace(/\/$/, '');

export interface CrmUnidade {
  idUnidadeOperacional: number;
  UnidadeOperacional: string;
  idPerfilEmpresa: number;
  perfilEmpresa: string;
}

export interface CrmSession {
  token: string;
  userId: number;
  idUnidadeOperacional: number;
  idPerfilEmpresa: number;
  perfilEmpresa: string;
  unidadeOperacional: string;
  unidades: CrmUnidade[];
  login: string;
}

export interface CrmUnitSelectionPending {
  partialToken: string;
  userId: number;
  login: string;
  unidades: CrmUnidade[];
}

export type CrmLoginResult =
  | { requiresUnitSelection: false; session: CrmSession }
  | ({ requiresUnitSelection: true } & CrmUnitSelectionPending);

async function parseJsonOrThrow(response: Response): Promise<any> {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.error || 'Erro ao comunicar com o CRM');
  }
  return body;
}

export const crmAuthService = {
  async login(login: string, senha: string): Promise<CrmLoginResult> {
    const response = await fetch(`${CRM_API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, senha }),
    });
    return parseJsonOrThrow(response);
  },

  async selectUnidade(pending: CrmUnitSelectionPending, idUnidadeOperacional: number): Promise<{ session: CrmSession }> {
    const response = await fetch(`${CRM_API_URL}/api/auth/select-unidade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partialToken: pending.partialToken,
        idUnidadeOperacional,
        login: pending.login,
        unidades: pending.unidades,
      }),
    });
    return parseJsonOrThrow(response);
  },
};

/**
 * Decodifica o payload do JWT sem checar assinatura — o mesmo padrão "Bearer estrito" que o
 * próprio CRM usa internamente (a validação real já aconteceu no login contra o painel).
 * Nunca logar o retorno: o campo `host` carrega a connection string do banco (ver aviso acima).
 */
export function decodeCrmToken(token: string): { exp?: number; [key: string]: unknown } | null {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isCrmTokenExpired(token: string): boolean {
  const payload = decodeCrmToken(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export default crmAuthService;
